import { Box, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"

// Every *_trend is an absolute delta in its metric's unit: positivity in
// fraction points, the others in counts or dollars.
export type KpiTrendUnit = "pts" | "count" | "money"

const formatMagnitude = (unit: KpiTrendUnit, delta: number) => {
  const abs = Math.abs(delta)
  if (unit === "pts") return `${(abs * 100).toFixed(1)} pts`
  if (unit === "money") return `$${Math.round(abs).toLocaleString("en-US")}`
  return Math.round(abs).toLocaleString("en-US")
}

const isZeroAfterRounding = (unit: KpiTrendUnit, delta: number) =>
  Number(formatMagnitude(unit, delta).replace(/[^\d.]/g, "")) === 0

const KpiTrend = ({
  unit,
  delta,
}: {
  unit: KpiTrendUnit
  delta: number | null
}) => {
  const { t } = useTranslation()

  // null = BE has no comparable prior period
  if (delta === null)
    return (
      <Typography variant="body3" color="mockup.neutral30" fontWeight={500}>
        {t("adminDashboard.nothingToCompareYet")}
      </Typography>
    )

  if (isZeroAfterRounding(unit, delta))
    return (
      <Typography variant="body3" color="mockup.neutral30" fontWeight={500}>
        {t("adminDashboard.noChangeVsPriorPeriod")}
      </Typography>
    )

  const isUp = delta > 0
  const arrowColor = isUp ? "mockup.success30" : "mockup.error50"

  return (
    <Typography variant="body3" color="mockup.neutral30" fontWeight={500}>
      <Box component="span" sx={{ color: arrowColor, fontWeight: 700 }}>
        <span aria-hidden="true">{isUp ? "▲" : "▼"}</span>
        <Box component="span" sx={visuallyHiddenSx}>
          {t(isUp ? "adminDashboard.trendUp" : "adminDashboard.trendDown")}
        </Box>{" "}
        {formatMagnitude(unit, delta)}
      </Box>{" "}
      {t("adminDashboard.vsPriorPeriod")}
    </Typography>
  )
}

export default KpiTrend
